import React from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { ChartNoAxesColumnIcon, LockIcon } from 'lucide-react'

import { Card, Skeleton } from '@repo/ui'
import { EmptyState, TourAdoption } from 'components'
import { useCompanyTourAnalytics } from 'hooks/api'
import { relativeOrDateTime } from 'utils'

/**
 * One company's product-tour adoption — the Tours tab of the internal company detail
 * page (VRG-516, against the endpoint VRG-515 built).
 *
 * ## It renders the SHARED `TourAdoption`, not its own funnel
 *
 * working-conventions §71: a detail tab shows the same thing as its standalone page,
 * scoped. `/internal/analytics` → Tours renders the same component with an unscoped
 * (or per-person) payload. So the rung maths, the retired-tour handling, and the
 * `started === 0` → em dash rule are one implementation, not two that agree until
 * someone edits one.
 *
 * What this does NOT borrow from that page is its Catalog and Anchor-contract
 * sections. Those are the global tour registry — byte-identical on every company — so
 * repeating them here would pad the tab with content that looks like this company's
 * data and is not.
 *
 * ## Two limits of the data, stated because they change how a number reads
 *
 *  - **Staff runs are excluded.** `tour_events.company_id` is null for Vera staff, so
 *    a company scope counts that company's own people only. These figures will not
 *    reconcile with the cross-company page's, and are not meant to.
 *  - **There is no over-time chart, and cannot be one from `tour_progress`.** That
 *    table has no `company_id` column at all — it is current-state, upserted in place.
 *    The funnel here comes from `tour_events`, which IS an append-only log, so the
 *    funnel is real; a progress-over-time line would have to be fabricated, so it is
 *    not drawn.
 */

interface CompanyToursPanelProps {
  companyId: string
}

const CompanyToursPanelSkeleton = () => (
  <Stack spacing={6}>
    <Card title="Adoption">
      <Stack spacing={3} sx={{ mt: 4 }}>
        <Skeleton height={18} width="40%" />
        <Skeleton height={14} lines={4} />
      </Stack>
    </Card>
    <Card title="Funnels">
      <Stack spacing={3} sx={{ mt: 4 }}>
        <Skeleton height={18} width="30%" />
        <Skeleton height={8} />
        <Skeleton height={8} />
        <Skeleton height={8} />
        <Skeleton height={8} />
      </Stack>
    </Card>
  </Stack>
)

export const CompanyToursPanel = ({ companyId }: CompanyToursPanelProps) => {
  const theme = useTheme()
  const { data, isLoading, error } = useCompanyTourAnalytics(companyId)

  if (isLoading) return <CompanyToursPanelSkeleton />

  if (error) {
    const status = (error as Error & { status?: number }).status
    return (
      <Card>
        <EmptyState
          icon={
            <LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />
          }
          title={
            status === 403
              ? 'You cannot view tour analytics'
              : 'Could not load tour analytics'
          }
          // Names a DIFFERENT permission from the Support tab, which is not a copy-paste
          // slip: this endpoint (and Ask stats, since 2026-10-08) is gated on
          // `internal_analytics_view`, Support on `internal_support_manage`. A staffer can
          // hold one and not the other, so this tab can be locked while Support is not.
          text={
            status === 403
              ? 'Tour analytics need the "View internal analytics" permission. Ask a platform admin to grant it.'
              : 'The request failed. Reload the page, or check the API is running.'
          }
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  const hasEvents = (data?.totalEvents ?? 0) > 0

  if (!hasEvents) {
    return (
      <Card>
        <EmptyState
          icon={
            <ChartNoAxesColumnIcon
              size={28}
              strokeWidth={1}
              color={theme.palette.text.secondary}
            />
          }
          title="No tour events for this company"
          // Says it in words on purpose: an empty log and a 0% completion rate look
          // identical drawn as a chart, and only one of them is a problem.
          text="Nobody at this company has started a tour in the last 30 days. That is an empty log, not a zero completion rate. Tours taken by Vera staff are not counted here."
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  return (
    <Stack spacing={6}>
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" spacing={3}>
        <Typography variant="body3" color="text.secondary">
          Last {data?.windowDays ?? 30} days
          {data?.computedAt ? ` · computed ${relativeOrDateTime(data.computedAt)}` : ''}
        </Typography>
      </Stack>
      <TourAdoption
        summary={data?.summary ?? []}
        steps={data?.steps ?? []}
        scopeNote="This company's own people only — tours taken by Vera staff are not counted. "
      />
    </Stack>
  )
}
